import React, { useState, useMemo } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { FAQS, FLAGSHIP_PRODUCTS } from '../constants';
import type { Service } from '../types';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { ArrowRight, ChevronDown, Zap, Sparkles, TrendingUp } from 'lucide-react';
import { trackPlausibleEvent } from '../utils/tracking';
import { MiniCaseStudies } from '../components/MiniCaseStudies';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll, StaggeredReveal } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { StatusWidget } from '../components/gaca/StatusWidget';

const AboutAuthorSection: React.FC = () => {
    return (
        <section id="about-author" className="container mx-auto px-4">
            <RevealOnScroll>
                <GlassCard variant="strong" className="text-center relative overflow-hidden">
                    <AnimatedGrid variant="dots" animated={false} />
                    <div className="relative z-10">
                        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                            <Zap className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="text-3xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
                            Cześć, tu Bartosz Gaca.
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto">
                            {/* 2026-08-12: było „…pod Twój KPI". Odbiorca tej strony nie mówi KPI.
                                Myślnik zamieniony na kropkę zgodnie ze standardem treści. */}
                            Buduję narzędzia, które robią robotę za Ciebie. Dosłownie.
                            <br className="hidden md:block" />
                            Ta strona to moje <strong className="text-text-primary-light dark:text-text-primary-dark">demo‑studio</strong>.
                            <br className="hidden md:block" />
                            Jeśli chcesz coś pod swoją firmę →
                            <a
                                href="https://bartoszgaca.pl/?utm_source=biznesbezklikania&utm_medium=about_section&utm_campaign=bbk%E2%86%92bg"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackPlausibleEvent('cta_click', { placement: 'about_section' })}
                                data-analytics-id="about_section_cta_bartoszgaca"
                                className="font-semibold text-primary-light dark:text-primary-dark hover:underline ml-1 inline-flex items-center gap-1"
                            >
                                BartoszGaca.pl
                                <ArrowRight className="w-4 h-4" />
                            </a>
                        </p>
                    </div>
                </GlassCard>
            </RevealOnScroll>
        </section>
    );
};

const HowItWorksSection: React.FC = () => {
    const steps = [
        {
            number: 1,
            title: 'Wgraj dane / użyj narzędzia',
            description: 'Przetestuj nasze dema, aby zobaczyć możliwości AI w akcji.',
        },
        {
            number: 2,
            title: 'Zobacz output / prototyp',
            description: 'Otrzymaj natychmiastowe wyniki i oceń potencjał dla Twojej firmy.',
        },
        {
            number: 3,
            title: 'Zamów wdrożenie',
            description: (
                <>
                    Chcesz to u siebie? Zleć gotowe wdrożenie na{' '}
                    <a
                        href="https://bartoszgaca.pl/?utm_source=biznesbezklikania&utm_medium=how_it_works&utm_campaign=bbk%E2%86%92bg"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackPlausibleEvent('cta_click', { placement: 'how_it_works' })}
                        data-analytics-id="how_it_works_cta_bartoszgaca"
                        className="font-semibold text-primary-light dark:text-primary-dark hover:underline"
                    >
                        BartoszGaca.pl
                    </a>
                </>
            ),
        },
    ];

    return (
        <section id="how-it-works" className="py-20">
            <div className="container mx-auto px-4">
                <RevealOnScroll className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                        Jak to działa? 3 proste kroki
                    </h2>
                    <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                        Prosty proces od testu do wdrożenia.
                    </p>
                </RevealOnScroll>

                <div className="relative max-w-5xl mx-auto">
                    {/* Animated connector line */}
                    <div className="absolute top-1/2 left-0 w-full h-0.5 hidden md:block" aria-hidden="true">
                        <div className="h-full bg-gradient-to-r from-primary-light/20 via-secondary-light/50 to-primary-light/20 dark:from-primary-dark/20 dark:via-secondary-dark/50 dark:to-primary-dark/20" />
                        <div
                            className="absolute top-0 left-0 h-full w-1/3 bg-gradient-to-r from-transparent via-primary-light dark:via-primary-dark to-transparent animate-shimmer"
                            style={{ backgroundSize: '200% 100%' }}
                        />
                    </div>

                    <div className="relative grid md:grid-cols-3 gap-8">
                        {steps.map((step, index) => (
                            <RevealOnScroll
                                key={step.number}
                                delay={index * 150}
                                className="text-center"
                            >
                                <GlassCard variant="medium" hover className="h-full">
                                    <div
                                        className={`
                                            mx-auto w-16 h-16 rounded-2xl
                                            bg-gradient-to-br from-primary-light to-secondary-light
                                            dark:from-primary-dark dark:to-secondary-dark
                                            text-white
                                            flex items-center justify-center mb-6
                                            text-2xl font-bold
                                            shadow-lg shadow-primary-light/30 dark:shadow-primary-dark/30
                                            animate-float
                                        `}
                                        style={{ animationDelay: `${index * 200}ms` }}
                                    >
                                        {step.number}
                                    </div>
                                    <h3 className="text-xl font-bold mb-3 text-text-primary-light dark:text-text-primary-dark">
                                        {step.title}
                                    </h3>
                                    <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                                        {step.description}
                                    </p>
                                </GlassCard>
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

const ServiceCard: React.FC<{ service: Service; index: number }> = ({ service, index }) => (
    <RevealOnScroll delay={index * 100}>
        <div
            className={`
                group relative
                bg-white/10 dark:bg-white/5 backdrop-blur-md
                p-6 rounded-2xl
                border border-white/20 dark:border-white/10
                shadow-xl hover:shadow-2xl
                hover:scale-[1.02] hover:bg-white/20 dark:hover:bg-white/10
                transition-all duration-300
                flex flex-col h-full
                overflow-hidden
            `}
        >
            {/* Gradient overlay on hover */}
            <div
                className={`
                    absolute inset-0 rounded-2xl
                    bg-gradient-to-br from-primary-light/10 to-secondary-light/10
                    dark:from-primary-dark/10 dark:to-secondary-dark/10
                    opacity-0 group-hover:opacity-100
                    transition-opacity duration-300
                    pointer-events-none
                `}
            />

            <div className="relative z-10 flex flex-col h-full">
                <div
                    className={`
                        mb-4 w-14 h-14 rounded-xl
                        bg-gradient-to-br from-primary-light/20 to-secondary-light/20
                        dark:from-primary-dark/20 dark:to-secondary-dark/20
                        flex items-center justify-center
                        group-hover:scale-110 transition-transform duration-300
                    `}
                >
                    {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 text-text-primary-light dark:text-text-primary-dark flex-grow">
                    {service.title}
                </h3>
                <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4 text-sm">
                    {service.description}
                </p>
                <ReactRouterDOM.Link
                    to={service.path}
                    className="font-semibold text-primary-light dark:text-primary-dark inline-flex items-center group/link mt-auto"
                >
                    Dowiedz się więcej
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/link:translate-x-1" />
                </ReactRouterDOM.Link>
            </div>
        </div>
    </RevealOnScroll>
);

const FAQAccordionItem: React.FC<{ faq: typeof FAQS[0]; index: number }> = ({ faq, index }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const contentId = `faq-${React.useId()}`;

    return (
        <RevealOnScroll delay={index * 50}>
            <div
                className={`
                    border-b border-border-light/50 dark:border-border-dark/50
                    ${isOpen ? 'bg-white/5 dark:bg-white/5' : ''}
                    transition-colors duration-300 rounded-lg
                `}
            >
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full flex justify-between items-center py-5 px-4 text-left group"
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                >
                    <span className="font-semibold text-lg text-text-primary-light dark:text-text-primary-dark group-hover:text-primary-light dark:group-hover:text-primary-dark transition-colors">
                        {faq.question}
                    </span>
                    <div
                        className={`
                            w-8 h-8 rounded-lg
                            bg-primary-light/10 dark:bg-primary-dark/10
                            flex items-center justify-center
                            transition-all duration-300
                            ${isOpen ? 'rotate-180 bg-primary-light/20 dark:bg-primary-dark/20' : ''}
                        `}
                    >
                        <ChevronDown className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                    </div>
                </button>
                <div
                    id={contentId}
                    className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
                >
                    <p className="pb-5 px-4 text-text-secondary-light dark:text-text-secondary-dark">
                        {faq.answer}
                    </p>
                </div>
            </div>
        </RevealOnScroll>
    );
};

const SimpleROICalculator: React.FC = () => {
    const [inputs, setInputs] = useState({
        employees: 5,
        salary: 5000,
        hoursSaved: 8,
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        setInputs((prev) => ({ ...prev, [id]: Number(value) }));
    };

    const results = useMemo(() => {
        const monthlyTimeSaved = inputs.employees * inputs.hoursSaved * 4.33;
        const hourlyRate = inputs.salary / 168;
        const monthlyCostSavings = monthlyTimeSaved * hourlyRate;

        return {
            monthlyTimeSaved: monthlyTimeSaved.toFixed(0),
            monthlyCostSavings: new Intl.NumberFormat('pl-PL', {
                style: 'currency',
                currency: 'PLN',
                minimumFractionDigits: 0,
            }).format(monthlyCostSavings),
        };
    }, [inputs]);

    return (
        <RevealOnScroll>
            <GlassCard variant="strong" className="relative overflow-hidden">
                <AnimatedGrid variant="lines" animated={false} />
                <div className="relative z-10 grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-white" />
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                                Oblicz Potencjalne Oszczędności
                            </h2>
                        </div>
                        <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">
                            Zobacz w 60 sekund, ile Twoja firma może zaoszczędzić dzięki automatyzacji. Wprowadź podstawowe dane, aby oszacować zwrot z inwestycji.
                        </p>
                        <div className="space-y-5">
                            <div>
                                <label htmlFor="employees" className="block text-sm font-medium mb-2 text-text-primary-light dark:text-text-primary-dark">
                                    Liczba pracowników (w powtarzalnych procesach)
                                </label>
                                <input
                                    type="number"
                                    id="employees"
                                    value={inputs.employees}
                                    onChange={handleInputChange}
                                    className="w-full p-3 bg-white/50 dark:bg-white/10 backdrop-blur-sm border border-white/20 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none transition-all"
                                />
                            </div>
                            <div>
                                <label htmlFor="salary" className="block text-sm font-medium mb-2 text-text-primary-light dark:text-text-primary-dark">
                                    Średnie miesięczne wynagrodzenie (PLN)
                                </label>
                                <input
                                    type="number"
                                    id="salary"
                                    value={inputs.salary}
                                    onChange={handleInputChange}
                                    step="500"
                                    className="w-full p-3 bg-white/50 dark:bg-white/10 backdrop-blur-sm border border-white/20 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none transition-all"
                                />
                            </div>
                            <div>
                                <label htmlFor="hoursSaved" className="block text-sm font-medium mb-2 text-text-primary-light dark:text-text-primary-dark">
                                    Oszczędność czasu na pracownika tygodniowo:{' '}
                                    <strong className="text-primary-light dark:text-primary-dark">{inputs.hoursSaved}h</strong>
                                </label>
                                <input
                                    type="range"
                                    id="hoursSaved"
                                    value={inputs.hoursSaved}
                                    onChange={handleInputChange}
                                    min="1"
                                    max="20"
                                    className="w-full h-2 bg-white/30 dark:bg-white/10 rounded-lg appearance-none cursor-pointer accent-primary-light dark:accent-primary-dark"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="text-center">
                        <GlassCard variant="medium" className="border-2 border-primary-light/30 dark:border-primary-dark/30">
                            <h3 className="text-lg font-semibold text-text-secondary-light dark:text-text-secondary-dark">
                                Szacowane miesięczne oszczędności
                            </h3>
                            <div className="my-6">
                                <p className="text-5xl md:text-6xl font-extrabold bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
                                    {results.monthlyCostSavings}
                                </p>
                                <p className="text-text-secondary-light dark:text-text-secondary-dark mt-2">
                                    ({results.monthlyTimeSaved} godzin odzyskanych)
                                </p>
                            </div>
                            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-6">
                                Wynik jest szacunkowy. Dokładna wartość zależy od specyfiki procesów w Twojej firmie.
                            </p>
                            <ReactRouterDOM.Link
                                to="/edukacja/kalkulator-roi-ai"
                                className="font-semibold text-primary-light dark:text-primary-dark inline-flex items-center group"
                            >
                                Użyj pełnego kalkulatora
                                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                            </ReactRouterDOM.Link>
                        </GlassCard>
                    </div>
                </div>
            </GlassCard>
        </RevealOnScroll>
    );
};

export const HomePage: React.FC = () => {
    usePageMetadata({
        title: 'Biznes bez Klikania | Koniec z ręczną robotą w firmie',
        description:
            'Przepisywanie danych, te same odpowiedzi do klientów, papierologia. Pokazujemy, co w małej firmie da się przestać robić ręcznie i ile to zajmie.',
        schema: {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: 'https://biznesbezklikania.pl/',
            potentialAction: {
                '@type': 'SearchAction',
                target: 'https://biznesbezklikania.pl/search?q={search_term_string}',
                'query-input': 'required name=search_term_string',
            },
        },
        ogData: {
            type: 'website',
        },
    });

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[85vh] flex items-center overflow-hidden" aria-labelledby="hero-heading">
                {/* Background Effects */}
                <ParticlesBackground variant="default" />
                <AnimatedBlob variant="mixed" />

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        {/* G.A.C.A. Status Badge */}
                        <div className="flex justify-center mb-8">
                            <StatusWidget variant="badge" />
                        </div>

                        <h1
                            id="hero-heading"
                            className={`
                                text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 tracking-tighter
                                bg-gradient-to-r from-primary-light via-secondary-light to-primary-light
                                dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark
                                bg-clip-text text-transparent
                                bg-[length:200%_200%] animate-gradient
                            `}
                        >
                            Koniec z ręczną robotą w Twojej firmie
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-10">
                            {/* 2026-08-12: było „Narzędziownia Automatyzacji AI" + „…ekspertowi, który
                                gwarantuje ROI". Pierwsze to nazwa kategorii, nie korzyść. Drugie to
                                obietnica, której nie umiem poprzeć liczbą, więc jej nie powtarzam. */}
                            Przepisywanie danych, te same odpowiedzi do klientów, papierologia.
                            Zaczynasz od jednej czynności, którą klikasz w kółko: testujesz gotowe
                            narzędzie albo zlecasz wdrożenie.
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                            <ReactRouterDOM.Link
                                to="/produkty"
                                data-analytics-id="hero_cta_produkty_diy"
                                className={`
                                    px-8 py-4 font-semibold
                                    text-primary-light dark:text-primary-dark
                                    bg-white/20 dark:bg-white/10 backdrop-blur-md
                                    border border-white/30 dark:border-white/20
                                    rounded-2xl
                                    hover:bg-white/30 dark:hover:bg-white/20
                                    hover:scale-[1.02]
                                    transition-all duration-300
                                    text-lg
                                    flex items-center gap-2
                                `}
                            >
                                <Sparkles className="w-5 h-5" />
                                Zobacz dema i tutoriale (DIY)
                            </ReactRouterDOM.Link>
                            <a
                                href="https://bartoszgaca.pl/?utm_source=biznesbezklikania&utm_medium=hero_cta&utm_campaign=bbk%E2%86%92bg"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackPlausibleEvent('cta_click', { placement: 'hero' })}
                                data-analytics-id="hero_cta_bartoszgaca"
                                className={`
                                    px-8 py-4 font-semibold text-white
                                    bg-gradient-to-r from-primary-light to-secondary-light
                                    dark:from-primary-dark dark:to-secondary-dark
                                    rounded-2xl
                                    hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30
                                    hover:scale-[1.02]
                                    transition-all duration-300
                                    text-lg
                                    inline-flex items-center gap-2
                                    group
                                    animate-glow
                                `}
                            >
                                <Zap className="w-5 h-5" />
                                Zleć wdrożenie (Done-For-You)
                                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                            </a>
                        </div>
                    </RevealOnScroll>
                </div>

                {/* Bottom fade */}
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            {/* Services Section */}
            <section
                id="services"
                className="py-20 bg-content-light/50 dark:bg-content-dark/50 relative"
                aria-labelledby="services-heading"
            >
                <AnimatedGrid variant="dots" animated={false} />

                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-16">
                        <h2
                            id="services-heading"
                            className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark"
                        >
                            Nasze Flagowe Rozwiązania
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-3xl mx-auto">
                            Skupiamy się na narzędziach, które przynoszą realne, mierzalne rezultaty. Oto rozwiązania najczęściej wybierane przez naszych klientów.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {FLAGSHIP_PRODUCTS.map((service, index) => (
                            <ServiceCard key={service.title} service={service} index={index} />
                        ))}
                    </div>
                </div>
            </section>

            {/* How it Works Section */}
            <HowItWorksSection />

            {/* Mini Case Studies Section */}
            <section className="py-20 bg-content-light/50 dark:bg-content-dark/50 relative">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/5 to-transparent dark:via-primary-dark/5 pointer-events-none" />
                <MiniCaseStudies />
            </section>

            {/* About Author Section */}
            <section className="py-20">
                <AboutAuthorSection />
            </section>

            {/* ROI Calculator Section */}
            <section className="py-20" aria-labelledby="roi-calculator-heading">
                <div className="container mx-auto px-4">
                    <SimpleROICalculator />
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 bg-content-light/50 dark:bg-content-dark/50" aria-labelledby="faq-heading">
                <div className="container mx-auto px-4 max-w-4xl">
                    <RevealOnScroll className="text-center mb-12">
                        <h2
                            id="faq-heading"
                            className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark"
                        >
                            Najczęstsze pytania
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4">
                            Wszystko co musisz wiedzieć o automatyzacji AI
                        </p>
                    </RevealOnScroll>
                    <GlassCard variant="subtle" className="divide-y divide-border-light/30 dark:divide-border-dark/30">
                        {FAQS.map((faq, index) => (
                            <FAQAccordionItem key={index} faq={faq} index={index} />
                        ))}
                    </GlassCard>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 relative overflow-hidden" aria-labelledby="cta-heading">
                <AnimatedBlob variant="purple" />
                <ParticlesBackground variant="minimal" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <h2
                            id="cta-heading"
                            className={`
                                text-3xl md:text-5xl font-bold mb-6
                                bg-gradient-to-r from-primary-light to-secondary-light
                                dark:from-primary-dark dark:to-secondary-dark
                                bg-clip-text text-transparent
                            `}
                        >
                            Gotowy na rewolucję w Twojej firmie?
                        </h2>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto mb-10">
                            Porozmawiajmy o tym, jak możemy pomóc Ci osiągnąć cele biznesowe dzięki AI. Konsultacja jest bezpłatna i niezobowiązująca.
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <ReactRouterDOM.Link
                            to="/kontakt"
                            className={`
                                inline-flex items-center gap-2
                                px-10 py-5 font-semibold text-white text-lg
                                bg-gradient-to-r from-primary-light to-secondary-light
                                dark:from-primary-dark dark:to-secondary-dark
                                rounded-2xl
                                hover:shadow-2xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30
                                hover:scale-[1.02]
                                transition-all duration-300
                                animate-glow
                            `}
                        >
                            <Zap className="w-6 h-6" />
                            Umów bezpłatną konsultację
                        </ReactRouterDOM.Link>
                    </RevealOnScroll>
                </div>
            </section>
        </>
    );
};
